/** 错误分类与重试边界。
 *
 * 「错误」在操作台上不是一句「加载失败」——操作员要能立刻分辨这四种：
 * 后端没起（network）、后端慢（timeout）、后端明确拒绝（http）、后端返回的形状
 * 不符合契约（contract）。四者的处置完全不同，所以分类在这里做，不由调用点各自猜。
 *
 * **重试边界**：只有「再试一次可能变好」的才重试。4xx 与契约违约是**确定的答案**，
 * 重试等于替后端掩盖问题；契约违约尤其不能重试——它意味着字段对不上，
 * 重试一万次也还是对不上。
 */

export type ApiErrorKind = "network" | "timeout" | "http" | "contract";

export interface ApiErrorInit {
  kind: ApiErrorKind;
  message: string;
  url?: string;
  status?: number;
  detail?: string;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly url: string | undefined;
  readonly status: number | undefined;
  readonly detail: string | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = "ApiError";
    this.kind = init.kind;
    this.url = init.url;
    this.status = init.status;
    this.detail = init.detail;
    if (init.cause !== undefined) this.cause = init.cause;
  }

  /** 再试一次是否可能变好。 */
  get retryable(): boolean {
    switch (this.kind) {
      case "network":
      case "timeout":
        return true;
      case "http":
        // 429 与 5xx 是「服务端现在不行」，4xx 是「这就是答案」；
        // 501 也是答案（接口未提供，重试不会变好，只会让屏多转几秒「正在取」）。
        return this.status === 429 || (this.status !== undefined && this.status >= 500 && this.status !== 501);
      case "contract":
        return false;
    }
  }
}

/** 契约违约：形状/字段与对接文档 §3 不符。**绝不回退成空表**——空表是「没有数据」
 * 这个业务结论，与「接口没按契约回答」是两回事。 */
export class ContractError extends ApiError {
  readonly body: unknown;

  constructor(message: string, url: string, body: unknown) {
    super({ kind: "contract", message, url, detail: summarise(body) });
    this.name = "ContractError";
    this.body = body;
  }
}

function summarise(body: unknown): string {
  try {
    const text = typeof body === "string" ? body : JSON.stringify(body);
    return text === undefined ? String(body) : text.slice(0, 300);
  } catch {
    return String(body);
  }
}

export function isRetryable(error: unknown): boolean {
  return error instanceof ApiError && error.retryable;
}

export interface ErrorText {
  title: string;
  why: string;
  hint: string;
}

/** 把错误翻成操作员能读的一句话 + 一句该干什么。 */
export function describeError(error: unknown): ErrorText {
  if (error instanceof ApiError) {
    const at = error.url ? `（${error.url}）` : "";
    switch (error.kind) {
      case "network":
        return {
          title: "连不上后端",
          why: `${error.message}${at}`,
          hint: "keel-server 常驻服务未启动或地址不对时会是这样。启动后端后点「重试」；不要据此判断账户状态。",
        };
      case "timeout":
        return {
          title: "后端超时",
          why: `${error.message}${at}`,
          hint: "请求发出但没在时限内返回。可以重试；若反复超时，先看后端进程与账目大小。",
        };
      case "http":
        if (error.status === 501) {
          return {
            title: "接口未提供",
            why: `接口未提供：${error.detail ?? error.message}${at}`,
            hint: "页面保留当前屏幕，但不会把未实现接口当成字段不符或业务空结果。",
          };
        }
        if (error.status === 401) {
          return {
            title: "令牌缺失或无效",
            why: `${error.message}${at}`,
            hint: "写接口要求令牌：去「设置」屏填写本机令牌后再试。只读页面不受影响。",
          };
        }
        return {
          title: `后端拒绝（HTTP ${error.status ?? "?"}）`,
          why: `${error.message}${at}${error.detail ? `\n${error.detail}` : ""}`,
          hint: error.status !== undefined && error.status < 500
            ? "这是后端给出的确定答案，重试无用；按上面的状态码查契约与权限。"
            : "服务端现在不行（也可能只是没启动）；可以重试。",
        };
      case "contract":
        return {
          title: "接口形状不符契约",
          why: `${error.message}${at}`,
          hint: `后端返回的字段与 docs/design/操作台对接开发文档.md §3 不符。这一屏**不显示任何数字**，因为对不上的字段无法判断是 0 还是缺失。`,
        };
    }
  }
  return {
    title: "未知错误",
    why: error instanceof Error ? `${error.name}：${error.message}` : String(error),
    hint: "看看浏览器控制台与后端日志。",
  };
}
